import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { AUTOCOMPLETE, ORDERS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCheckCircle, faExchange } from '@fortawesome/pro-regular-svg-icons';

const ReviewPickingForm = NiceModal.create(({ ID }) => {
	const axios = useAxiosPrivate();
	const modal = useModal();
	const dispatch = useDispatch();
	const formMethods = useForm();
	const { showError, showSuccess } = useAlert();
	const colSpan = useMemo(() => ({ md: 6 }), []);

	const { handleSubmit, reset, watch, setValue } = formMethods;

	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const fetchOrderDetails = async () => {
		setIsLoading(true);

		await axios
			.post(ORDERS.GET_ORDER_BY_ID, { ID })
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						orderID: data?.orderDetails.orderID,
						packingStatus: data?.orderDetails.packingStatus,
						warehouseID: data?.customerDetails.warehouseID
					});
					setData(data?.itemsDetails);
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setIsLoading(false);
	};

	useEffect(() => {
		ID && fetchOrderDetails();
	}, [ID]);

	const missingContentItems = async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.REVIEW_PICKING_CONFIRM_MISSING_ITEMS, {
				otoOrderID: ID
			});
			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListsInPage());
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
		modal.remove();
	};

	const markAsPicked = async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.REVIEW_PICKING_MARK_AS_PICKED, {
				otoOrderID: ID
			});
			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListsInPage());
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
		modal.remove();
	};

	const sendBackToPicking = async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.REVIEW_PICKING_SEND_BACK_TO_PICKING, {
				otoOrderID: ID
			});
			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListsInPage());
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
		modal.remove();
	};

	const columns = useMemo(
		() => [
			{
				header: 'productName',
				renderCell: ({ row }) => (
					<ProductImage
						data={{
							name: row.original.name,
							imageURL: row.original.image
						}}
					/>
				),
				size: 150,
				showOnMobile: true
			},
			{
				header: 'SKU',
				accessorKey: 'SKU',
				size: 100,
				showOnMobile: true
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 100
			},
			{
				header: 'qtyPicked',
				accessorKey: 'qty_picked',
				size: 100
			}
		],
		[data]
	);

	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-reviewPickingForm">
			<ModalTitle>
				<Typography variant="p1">reviewPickingForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="orderID" colSpan={colSpan} isLoading={isLoading}>
						<StringInput name="orderID" disabled />
					</FormItem>

					<FormItem colSpan={colSpan} label="warehouse" isLoading={isLoading}>
						<AutoComplete
							disabled
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							setValue={(value) => {
								setValue(`warehouseID`, value.ID, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('warehouseID')}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
						/>
					</FormItem>

					<FormItem label="packingStatus" colSpan={colSpan} isLoading={isLoading}>
						<EnumSelect value={watch('packingStatus')} disabled />
					</FormItem>

					<FormItem>
						<Table data={data} columns={columns} loading={isLoading} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						modal.remove();
					}}
					variant="outline"
				/>

				<Button
					onClick={handleSubmit(sendBackToPicking, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faExchange}
					loading={isLoading}
					label="sendBackToPicking"
					variant="black"
				/>

				<Button
					onClick={handleSubmit(missingContentItems, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faExchange}
					loading={isLoading}
					label="confirmMissingItems"
					variant="blackOutline"
				/>

				<Button
					onClick={handleSubmit(markAsPicked, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faCheckCircle}
					loading={isLoading}
					label="markAsPicked"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default ReviewPickingForm;
